import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { CloseIconCircle, CrossIcon, Delete2Icon, PlusSignIcon } from "../../assets/icons";
import useAllergens from "../../hooks/useAllergens";
import LanguageSwitcher, { Language } from "../LanguageSwitcher";
import {
  AdminMealMultiLangResponse,
  AMMLMealIngredientResponse,
  CreateMealRequest,
  IngredientResponse,
  MealPortionDto,
  MMealResponsePortionUnitEnum,
  UploadImageImageTypeEnum,
} from "../../openapi/client";
import { IDialogProps } from "../../openapi/interfaces";
import {
  cdnUrl,
  checkEmpty,
  checkIf,
  findImageByType,
  findImageId,
  generateRandom,
  maxFileSize,
  slugify,
} from "../../utils";
import { RadioBoxCustom } from "../RadioBox";
import Tagging from "../Tagging";
import Transition from "../Transition";
import useValidation from "../Validation";
import Api from "../../openapi";
import { useDispatch } from "react-redux";
import { setNotification } from "../../store/notificationSlice";
import { LoadingButton } from "@mui/lab";
import useIngredients from "../../hooks/useIngredients";
import { DropzoneArea } from "mui-file-dropzone";
import { upload } from "../PlanCrud";
import { Toggle } from "../Switch";

interface IMealCrud extends IDialogProps {
  meal?: AdminMealMultiLangResponse & { name?: any };
}
interface UpdateMealRequest extends CreateMealRequest {
  id?: string;
}
interface IPortionItem {
  error?: boolean;
  value?: number;
  message?: string;
}
interface IMealPortionCreateDto {
  id?: any;
  default?: any;
  portion?: IPortionItem;
  calories?: IPortionItem;
  protein?: IPortionItem;
  carbs?: IPortionItem;
  fat?: IPortionItem;
}

interface IIngredient extends IngredientResponse {
  removable?: boolean;
  amount?: number;
}
interface IIngredients {
  options: IngredientResponse[];
  onChange: (event: MouseEvent, newValue: any) => void;
}

export function Ingredients(props: IIngredients) {
  const [value, setValue] = useState<IngredientResponse | null>();
  const [inputValue, setInputValue] = useState("");

  const handleOnchange = (event: any, newValue: IngredientResponse | null) => {
    event.stopPropagation();
    setValue(newValue);
    props.onChange(event, newValue);
  };

  return (
    <Autocomplete
      value={value}
      onChange={handleOnchange}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={props.options}
      getOptionLabel={(option) => String(option.name?.en)}
      sx={{
        flex: 1,
        "& .MuiOutlinedInput-root": { margin: 0, height: "auto" },
        "& fieldset": { border: "none", "& .MuiAutocomplete-endAdornment": { display: "none" } },
      }}
      renderInput={(params) => <TextField {...params} placeholder="Add Ingredient" />}
    />
  );
}

export default function MealCrud(props: IMealCrud) {
  const dummyPortion = {
    id: generateRandom(),
    default: true,
    portion: {
      value: undefined,
      error: true,
      message: "Invalid value",
    },
    calories: {
      value: undefined,
      error: true,
      message: "Invalid value",
    },
    protein: {
      value: undefined,
      error: true,
      message: "Invalid value",
    },
    carbs: {
      value: undefined,
      error: true,
      message: "Invalid value",
    },
    fat: {
      value: undefined,
      error: true,
      message: "Invalid value",
    },
  };
  // console.log(props);
  const blendWithDummyPortion = (portions: MealPortionDto[]) => {
    return portions.map((portion) => {
      let temp: IMealPortionCreateDto = {};
      const { default: defaultPortion, ...payload } = portion;
      Object.entries(payload).forEach(([key, value]) => {
        // const numberRegex = /^[-+]?[0-9]+(\.[0-9]+)*$/;
        const numberRegex = /^\d+(\.\d{1,2})?$/;
        temp[key as keyof IMealPortionCreateDto] = {
          value,
          error: !Boolean(value?.toString().match(numberRegex)),
          message: "Invalid value",
        };
      });
      temp.id = generateRandom();
      temp.default = Boolean(defaultPortion);
      return temp;
    });
  };

  const [formDirty, setFormDirty] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>("en");
  const [meal, setMealEdit] = useState<any>({
    name: { en: "", ar: "" },
    slug: "",
    description: { en: "", ar: "" },
    portionUnit: "",
    ingredients: [],
    allergens: "",
    tags: [],
    images: [],
    portionCustomizable: false,
  });
  const dispatch = useDispatch();
  const { validation, setValidation } = useValidation(meal);
  const [portions, setPortions] = useState<IMealPortionCreateDto[]>(
    props.meal?.portions ? blendWithDummyPortion(Array.from(props.meal?.portions)) : [dummyPortion]
  );
  const [portionsError, setPortionsError] = useState({ show: false, error: false });
  const allergenList = useAllergens();
  const [allergens, setAllergens] = useState<Array<string>>([]);
  const ingredientList = useIngredients();
  const [ingredients, setIngredients] = useState<AMMLMealIngredientResponse[]>([]);
  const [portionOnIngredient, setPortionOnIngredient] = useState<string>("");
  const [portionRemovableValue, setPortionRemovableValue] = useState(true);
  const [ingredientSelection, setIngredientSelection] = useState<IngredientResponse>();
  const [portionOnIngredientError, setPortionOnIngredientError] = useState({
    show: false,
    error: false,
    message: "Invalid value",
  });

  // console.log("ingredients", ingredients);

  const [defaultPortion, setDefaultPortion] = useState<number>(dummyPortion.id);
  const [loading, setLoading] = useState(false);

  const [files, setFiles] = useState<any>();
  const [imageRemoved, setImageRemoved] = useState(false);
  const [imageValidation, setImageValidation] = useState({
    error: false,
    show: false,
    message: "",
    resolution: { w: 0, h: 0 },
    defaultRatio: 365 / 220,
  });

  const imageRemoval = () => {
    setImageRemoved(true);
    // Meal image is optional, we can remove the following validation
    // setImageValidation((state) => ({
    //   ...state,
    //   error: true,
    //   message: "No image selected for the replacement",
    // }));
  };

  const fileChange = (files: any) => {
    // console.log("fileChange", files);
    setFiles(files);
    if (files && files.length) {
      const img = new Image();
      img.src = window.URL.createObjectURL(files[0]);

      img.onload = function () {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        const ratio = width / height;
        setImageValidation((state) => {
          let message = "";
          let error = false;
          let show = state.show;
          // RJM-793 Default Ratio Check removed
          // if (ratio > state.defaultRatio || ratio < state.defaultRatio - 0.3) {
          //   message = `Please set the width/height ratio around ${
          //     Math.round(state.defaultRatio * 100) / 100
          //   }. Currently it is ${Math.round(ratio * 100) / 100}`;
          //   error = true;
          //   show = true;
          // }
          return {
            ...state,
            resolution: { w: width, h: height },
            message,
            error,
            show,
          };
        });
      };
      if (files[0].size > maxFileSize) {
        setImageValidation((state) => ({
          ...state,
          message: `Maximum image size is ${maxFileSize / 1024}KB`,
          error: true,
          show: true,
        }));
      }
    }
  };

  // console.log(imageValidation);
  // console.log(files);

  const handleFieldChange = (field: any, value?: any) => {
    const fieldValue = value;
    setFormDirty(true);
    if (typeof field === "string") {
      if (field === "slug") {
        value = slugify(value);
      }
      if (field === "tags") {
        value = Array.from(
          new Set(
            value
              .toString()
              .split(",")
              .map((item: string) => item.trim())
          )
        );
      }
      setMealEdit((state: any) => ({ ...state, [field]: value }));
    } else {
      let slugRefactor: string;
      if (field.name) {
        slugRefactor = slugify(field.name.en);
        setMealEdit((state: any) => ({ ...state, ...field, slug: slugRefactor }));
      } else {
        setMealEdit((state: any) => ({ ...state, ...field }));
      }
    }

    // A check for Portion values to stay btw 0.5 and 9
    if (field === "portionUnit") {
      setPortions((state) => {
        let temp = [...state];
        const numberRegex = /^\d+(\.\d{1,2})?$/;
        temp.forEach((_, index) => {
          const value = _.portion?.value?.toString();
          const checkIfNumber = Boolean(value?.match(numberRegex));
          const checkOutOfRange = Number(value) < 0.5 || Number(value) > 9;
          let message = "Invalid value";
          let errorFlag = false;
          let error = false;
          if (fieldValue === MMealResponsePortionUnitEnum.Portion) {
            if (checkIfNumber) {
              if (checkOutOfRange) {
                message = "Value must be between 0.5 and 9";
                error = true;
              }
            } else {
              error = true;
            }
          } else if (!checkIfNumber) {
            error = true;
          }

          temp[index].portion!.error = error;
          temp[index].portion!.message = message;
        });
        return temp;
      });
    }
  };

  // console.log("meal", meal);
  // console.log("portions", portions);
  // console.log("validation", validation);

  const handlePortionChange = (portion: IMealPortionCreateDto, field: string, value: string) => {
    // console.log(portion.id);
    setPortions((state) => {
      let temp = [...state];
      const index = temp.findIndex((item) => item.id === portion.id);
      const fieldRef = field as keyof IMealPortionCreateDto;
      const numberRegex = /^\d+(\.\d{1,2})?$/;

      // check if portion field is between values : 0.5-9
      if (fieldRef === "portion") {
        const checkIfNumber = Boolean(value.match(numberRegex));
        const checkOutOfRange = Number(value) < 0.5 || Number(value) > 9;
        let message = "";
        let errorFlag = false;
        const checkPortionUnitInfo = meal?.portionUnit === MMealResponsePortionUnitEnum.Portion;

        if (meal?.portionUnit === MMealResponsePortionUnitEnum.Portion) {
          if (checkIfNumber) {
            if (checkOutOfRange) {
              message = "Value must be between 0.5 and 9";
            }
          } else {
            message = "Invalid value";
          }
        } else if (checkEmpty(meal?.portionUnit)) {
          message = "Choose portion unit";
          errorFlag = true;
        }

        temp[index].portion!.value = value as any;
        temp[index].portion!.error = !checkIfNumber || (checkOutOfRange && checkPortionUnitInfo) || errorFlag;
        temp[index].portion!.message = message;
      } else {
        temp[index][fieldRef].value = value;
        temp[index][fieldRef].error = !Boolean(value.match(numberRegex));
        temp[index][fieldRef].message = "Invalid value";
      }

      // check for the same amount of portion units
      if (fieldRef === "portion") {
        const filter = temp.filter((portion) => portion.portion?.value === temp[index][fieldRef]?.value);
        if (Number(value) <= 0) {
          temp[index][fieldRef]!.error = true;
          temp[index][fieldRef]!.message = "Invalid value";
        }
        if (filter.length > 1) {
          filter.forEach((item) => {
            const findIndex = temp.findIndex((i) => i.id === item.id);
            temp[findIndex].portion!.error = true;
            temp[findIndex].portion!.message = "Same unit value";
          });
        } else {
          temp.forEach((item, key) => {
            if (key !== index) {
              item.portion!.error = !Boolean(String(item.portion?.value).match(numberRegex));
              item.portion!.message = "Invalid value";
            }
          });
        }
      }

      return temp;
    });
  };
  const handleDefaultPortionChange = (id: string) => {
    setPortions((state) => {
      let temp = [...state];
      const index = temp.findIndex((portion) => parseInt(portion.id) === parseInt(id));
      state.forEach((_, key) => {
        temp[key].default = index === key;
      });
      return temp;
    });
    setDefaultPortion(Number(id));
  };

  const addPortion = () => {
    setPortions((state) => [...state, { ...dummyPortion, default: false }]);
  };

  const deletePortion = (portion: IMealPortionCreateDto) => {
    setPortions((state) => {
      let temp = [...state];
      const index = temp.findIndex((item) => item.id === portion.id);
      const assignOther = Boolean(temp[index].default);
      temp.splice(index, 1);
      if (temp.length === 1 || assignOther) {
        temp[0].default = true;
      }
      return temp;
    });
  };

  const updateAllergens = (_: any, selection: string) => {
    setAllergens((state) => {
      let temp = [...state];
      if (!temp.includes(selection)) {
        temp.push(selection);
      }
      return temp;
    });
  };

  const deleteAllergen = (selection: string) => {
    setAllergens((state) => {
      let temp = [...state];
      const findIndex = temp.indexOf(selection);
      temp.splice(findIndex, 1);
      return temp;
    });
  };

  const deleteIngredient = (selection: AMMLMealIngredientResponse) => {
    setIngredients((state) => {
      let temp = [...state];
      const findIndex = temp.findIndex((i) => i.id === selection.id);
      temp.splice(findIndex, 1);
      return temp;
    });
  };

  const handleIngredientRemovable = (_: any, selection: AMMLMealIngredientResponse) => {
    setIngredients((state) => {
      let temp = [...state];
      const findIndex = temp.findIndex((i) => i.id === selection.id);
      temp[findIndex].removable = !temp[findIndex].removable;
      return temp;
    });
  };

  const handlePortionOnIngredient = (value: string) => {
    const numberRegex = /^\d+(\.\d{1,2})?$/;
    let numberError = !Boolean(value.match(numberRegex));
    let maxError = !numberError && Number(value) > 999;
    setPortionOnIngredient(value);
    if (!checkIf(value) || checkEmpty(value)) {
      setPortionOnIngredientError((state) => ({ ...state, error: false }));
    } else {
      setPortionOnIngredientError((state) => ({
        ...state,
        error: numberError || maxError,
        message: numberError ? "Invalid value" : maxError ? "Max 999 is allowed" : "",
      }));
    }
  };

  const handleIngredientChange = (_: any, selection: IngredientResponse) => {
    setIngredientSelection(selection);
  };

  const addIngredient = () => {
    //// updateIngredients(...args);
    // console.log("ingredient details");
    // console.log("portionOnIngredient", portionOnIngredient);
    const portionAmount =
      checkEmpty(portionOnIngredient) || !checkIf(portionOnIngredient) ? undefined : Number(portionOnIngredient);
    // console.log("portionRemovableValue", portionRemovableValue);
    // console.log("ingredientSelection", ingredientSelection);
    // console.log("portionOnIngredientError", portionOnIngredientError);
    // console.log("portionUnit");

    if (portionOnIngredientError.error) {
      setPortionOnIngredientError((state) => ({ ...state, show: true }));
      return;
    }

    setIngredients((state) => {
      let temp = [...state];
      if (ingredientSelection && !temp.find((i) => i.id === ingredientSelection.id)) {
        temp.push({
          ...ingredientSelection,
          name: ingredientSelection.name?.en,
          amount: portionAmount,
          removable: portionRemovableValue,
        });
      }
      return temp;
    });
  };

  const saveChanges = async () => {
    if (validation.error || portionsError.error || imageValidation.error) {
      setValidation((state) => ({ ...state, show: true }));
      setPortionsError((state) => ({ ...state, show: true }));
      setImageValidation((state) => ({ ...state, show: true }));
      return;
    }

    let MealReqDto: any = {
      ...meal,
      allergens,
      ingredients,
      portions: portions.map((portion) => {
        const { id, default: defaultPortion, ...payload } = portion;
        let portionDto: any = {};
        Object.entries(payload).forEach(([key, portionItem]) => {
          portionDto[key] = Number(portionItem.value);
        });
        if (Boolean(defaultPortion)) portionDto.default = true;
        return portionDto;
      }),
    };

    try {
      setLoading(true);

      if (files && files.length) {
        const uploadedImage = await upload(UploadImageImageTypeEnum.Meal, files[0]);
        if (uploadedImage) {
          MealReqDto.imageId = uploadedImage.id;
        }
      } else {
        if (imageRemoved) {
          MealReqDto.imageId = null;
        } else {
          MealReqDto.imageId = findImageId(meal.images, "THUMBNAIL");
        }
      }

      let message;
      if (props.create) {
        message = "Meal has been created successfully";
        await Api.AdminMeal.createMeal(MealReqDto);
        resetForm();
      } else if (props.edit) {
        message = "Meal has been updated successfully";
        const { id, ...UpdateMealReqDto } = MealReqDto;
        await Api.AdminMeal.updateMeal(meal.id, UpdateMealReqDto);
      }
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message,
        })
      );
      props.onConfirm(true);
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setLoading(false);
  };

  const resetForm = () => {
    setSelectedLanguage("en");
    setMealEdit({
      name: { en: "", ar: "" },
      slug: "",
      description: { en: "", ar: "" },
      portionUnit: "",
      ingredients: [],
      allergens: "",
      tags: [],
      portionCustomizable: false,
    });
    setPortions([dummyPortion]);
    setValidation((state) => ({ ...state, show: false }));
    setPortionsError({ show: false, error: false });
    setAllergens([]);
    setIngredients([]);
    setIngredientSelection(undefined);
    setPortionOnIngredient("");
    setPortionOnIngredientError({
      show: false,
      error: false,
      message: "Invalid value",
    });
    setImageRemoved(false);
  };

  const handlePanelClose = (...args: any) => {
    resetForm();
    props.onClose(args);
  };

  useEffect(() => {
    let error = portions.some((portion) => {
      return Object.keys(portion).some((item) => portion[item as keyof IMealPortionCreateDto]?.error);
    });
    setPortionsError((state) => ({ ...state, error }));
    setDefaultPortion((state) => {
      const find = portions.find((portion) => Boolean(portion.default));
      return Number(find?.id);
    });
  }, [portions]);

  useEffect(() => {
    props.meal && setMealEdit(props.meal);
    props.meal?.portions && setPortions(blendWithDummyPortion(Array.from(props.meal.portions)));
    props.meal?.allergens && setAllergens(Array.from(props.meal.allergens));
    props.meal?.ingredients && setIngredients(Array.from(props.meal?.ingredients) as AMMLMealIngredientResponse[]);
  }, [props.meal]);

  // console.log("portionsError", portionsError);
  // console.log("ingredients", ingredients);
  // console.log("meal", meal);

  return (
    <Fragment>
      <Dialog
        open={props.open}
        TransitionComponent={Transition}
        onClose={handlePanelClose}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "16px",
            boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
            padding: "24px",
            width: "880px",
            maxWidth: "880px",
          },
          "& .MuiDialogTitle-root": {
            padding: "0 48px 16px 0",
          },
          "& .MuiDialogContent-root,& .MuiDialogActions-root": {
            padding: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "text.dark",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography component="span" sx={{ fontSize: "16px", fontWeight: 500 }}>
            {props.title}
          </Typography>
          <LanguageSwitcher value={selectedLanguage} onChange={setSelectedLanguage} />
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handlePanelClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.fontLight2",
          }}
        >
          <CrossIcon />
        </IconButton>
        <DialogContent>
          <FormControl sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                <Box
                  sx={{
                    width: "170px",
                  }}
                >
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      Image
                    </Typography>

                    {checkIf(findImageByType(meal.images, "THUMBNAIL")) && !imageRemoved ? (
                      <Box
                        sx={{
                          borderRadius: "12px",
                          width: "170px",
                          height: "140px",
                          background: `url(${cdnUrl + findImageByType(meal.images, "THUMBNAIL")})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "center",
                          paddingBottom: "12px",
                        }}
                      >
                        <Button
                          onClick={imageRemoval}
                          sx={{
                            borderRadius: "4px",
                            color: "#fff",
                            fontSize: "12px",
                            fontWeight: 500,
                            lineHeight: "13px",
                            padding: "4px",
                            backgroundColor: "text.style",
                            boxShadow: "0px 3px 6px 0px rgba(200, 38, 38, 0.25)",
                            "&:hover": { backgroundColor: "text.style" },
                          }}
                        >
                          Delete Img
                        </Button>
                      </Box>
                    ) : (
                      <Fragment>
                        <DropzoneArea
                          dropzoneClass={`MuiDropzone MealDropzone ${files?.length ? "files" : ""}`}
                          dropzoneText="Drag files here or click to add"
                          maxFileSize={maxFileSize}
                          fileObjects={files}
                          filesLimit={1}
                          acceptedFiles={["image/*"]}
                          onDropRejected={(files) => {}}
                          onChange={fileChange}
                        />
                        {imageValidation.show && imageValidation.error ? (
                          <FormHelperText sx={{ color: "text.error", margin: 0 }}>
                            {imageValidation.message}
                          </FormHelperText>
                        ) : null}
                      </Fragment>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", flex: 1 }}>
                  <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}>
                    <Box className="form-section">
                      <Box sx={{ display: "flex" }}>
                        <Typography component="span" className="form-label">
                          Name {selectedLanguage === "en" ? "(English)" : "(Arabic)"}
                        </Typography>
                        <Typography component="span" className="required-label">
                          {selectedLanguage === "en" ? "*" : ""}
                        </Typography>
                      </Box>

                      <TextField
                        className="form-field"
                        value={meal?.name?.[selectedLanguage] || ""}
                        error={validation.show && validation.name?.[selectedLanguage]?.error}
                        helperText={
                          validation.show && validation.name?.[selectedLanguage]?.error
                            ? validation.name[selectedLanguage]?.helperText
                            : undefined
                        }
                        onChange={(e) =>
                          handleFieldChange({
                            name: { ...meal.name, [selectedLanguage]: e.target.value },
                          })
                        }
                        inputProps={{
                          dir: selectedLanguage === "ar" ? "rtl" : "ltr",
                        }}
                      />
                    </Box>
                    <Box className="form-section">
                      <Box sx={{ display: "flex" }}>
                        <Typography component="span" className="form-label">
                          Slug
                        </Typography>
                        <Typography component="span" className="required-label">
                          *
                        </Typography>
                      </Box>

                      <TextField
                        className="form-field"
                        value={meal?.slug}
                        error={validation.show && validation.slug?.error}
                        helperText={validation.show && validation.slug?.error ? validation.slug.helperText : undefined}
                        onChange={(e) => handleFieldChange("slug", e.target.value)}
                      />
                    </Box>
                  </Box>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      Description {selectedLanguage === "en" ? "(English)" : "(Arabic)"}
                    </Typography>
                    <TextField
                      className="form-field"
                      multiline
                      sx={{ "& .MuiInputBase-root": { height: "auto" } }}
                      value={meal?.description?.[selectedLanguage] || ""}
                      error={validation.show && validation.description?.[selectedLanguage]?.error}
                      helperText={
                        validation.show && validation.description?.[selectedLanguage]?.error
                          ? validation.description[selectedLanguage]?.helperText
                          : undefined
                      }
                      onChange={(e) =>
                        handleFieldChange({
                          description: { ...meal.description, [selectedLanguage]: e.target.value },
                        })
                      }
                      inputProps={{
                        dir: selectedLanguage === "ar" ? "rtl" : "ltr",
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box className="form-section">
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" className="form-label">
                    Unit
                  </Typography>
                  <Typography component="span" className="required-label">
                    *
                  </Typography>
                </Box>

                <RadioGroup
                  className="form-field"
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    "& > label": { flex: "0 !important", background: "none !important", padding: "12px 0 !important" },
                    "& > label > span": { fontWeight: "400 !important" },
                  }}
                  value={meal?.portionUnit || null}
                  onChange={(e) => handleFieldChange("portionUnit", e.target.value)}
                >
                  <FormControlLabel
                    className={meal?.portionUnit === MMealResponsePortionUnitEnum.Gram ? "selected" : ""}
                    value={MMealResponsePortionUnitEnum.Gram}
                    control={<RadioBoxCustom />}
                    label="Gram"
                  />
                  <FormControlLabel
                    className={meal?.portionUnit === MMealResponsePortionUnitEnum.Portion ? "selected" : ""}
                    value={MMealResponsePortionUnitEnum.Portion}
                    control={<RadioBoxCustom />}
                    label="Portion"
                  />
                  <FormControlLabel
                    className={meal?.portionUnit === MMealResponsePortionUnitEnum.Piece ? "selected" : ""}
                    value={MMealResponsePortionUnitEnum.Piece}
                    control={<RadioBoxCustom />}
                    label="Piece"
                  />
                </RadioGroup>
                {validation.show && validation.portionUnit?.error ? (
                  <FormHelperText sx={{ color: "text.error" }}>{validation.portionUnit.helperText}</FormHelperText>
                ) : null}
              </Box>

              {/* {Array.from(portions).map((portion, key) => (
                <Box
                  key={key}
                  className="portions-container"
                  sx={{ display: "flex", alignItems: "end", columnGap: "12px", marginBottom: "12px" }}
                >
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      Portion
                    </Typography>
                    <TextField
                      className="form-field"
                      value={portion.portion?.value}
                      error={portionsError.show && portion.portion?.error}
                      helperText={portionsError.show && portion.portion?.error ? portion.portion.message : undefined}
                      onChange={(e) => handlePortionChange(portion, "portion", e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment sx={{ "& p": { textTransform: "capitalize" } }} position="end">
                            {meal?.portionUnit ? meal.portionUnit.toLowerCase() : ""}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      Calories
                    </Typography>
                    <TextField
                      className="form-field"
                      value={portion.calories?.value}
                      error={portionsError.show && portion.calories?.error}
                      helperText={portionsError.show && portion.calories?.error ? portion.calories.message : undefined}
                      onChange={(e) => handlePortionChange(portion, "calories", e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">Cal</InputAdornment>,
                      }}
                    />
                  </Box>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      Protein
                    </Typography>
                    <TextField
                      className="form-field"
                      value={portion.protein?.value}
                      error={portionsError.show && portion.protein?.error}
                      helperText={portionsError.show && portion.protein?.error ? portion.protein.message : undefined}
                      onChange={(e) => handlePortionChange(portion, "protein", e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">Gr</InputAdornment>,
                      }}
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    />
                  </Box>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      Carbs
                    </Typography>
                    <TextField
                      className="form-field"
                      value={portion.carbs?.value}
                      error={portionsError.show && portion.carbs?.error}
                      helperText={portionsError.show && portion.carbs?.error ? portion.carbs.message : undefined}
                      onChange={(e) => handlePortionChange(portion, "carbs", e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">Gr</InputAdornment>,
                      }}
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    />
                  </Box>
                  <Box className="form-section">
                    <Typography component="span" className="form-label">
                      Fat
                    </Typography>
                    <TextField
                      className="form-field"
                      value={portion.fat?.value}
                      error={portionsError.show && portion.fat?.error}
                      helperText={portionsError.show && portion.fat?.error ? portion.fat.message : undefined}
                      onChange={(e) => handlePortionChange(portion, "fat", e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">Gr</InputAdornment>,
                      }}
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    />
                  </Box>
                  {portions.length > 1 && (
                    <Box className="form-section" sx={{ flex: 0 }}>
                      <IconButton
                        sx={{
                          backgroundColor: "text.gray",
                          color: "white",
                          padding: "12px",
                          width: "50px",
                          height: "50px",
                          borderRadius: "8px",
                          "&:hover": { backgroundColor: "text.gray2" },
                        }}
                        onClick={() => deletePortion(portion)}
                      >
                        <Delete2Icon width={24} height={24} />
                      </IconButton>
                    </Box>
                  )}
                  {key === 0 && (
                    <Box className="form-section" sx={{ flex: 0 }}>
                      <IconButton
                        sx={{
                          backgroundColor: "secondary.main",
                          color: "white",
                          padding: "12px",
                          width: "50px",
                          height: "50px",
                          borderRadius: "8px",
                          "&:hover": { backgroundColor: "secondary.dark" },
                        }}
                        onClick={addPortion}
                      >
                        <PlusSignIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              ))} */}

              <RadioGroup
                className="form-field"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "24px",
                  "& > label": { margin: 0, columnGap: "8px" },
                  "& > label > span": { padding: 0 },
                  "& > label > span > svg": { width: "24px", height: "24px" },
                }}
                value={defaultPortion || null}
                onChange={(e) => handleDefaultPortionChange(e.target.value)}
              >
                {Array.from(portions).map((portion, key) => (
                  <FormControlLabel
                    key={portion.id}
                    className={`${portion.default ? "selected" : ""}`}
                    value={portion.id}
                    control={<RadioBoxCustom />}
                    label={
                      <Box
                        key={portion.id}
                        onClick={(e) => e.preventDefault()}
                        className="portions-container"
                        sx={{ display: "flex", alignItems: "end", columnGap: "12px" }}
                      >
                        <Box className="form-section" sx={{ flex: 2 }}>
                          <Typography component="span" className="form-label">
                            Portion Unit
                          </Typography>
                          <TextField
                            className="form-field"
                            value={portion.portion?.value}
                            error={portionsError.show && portion.portion?.error}
                            helperText={
                              portionsError.show && portion.portion?.error ? portion.portion.message : undefined
                            }
                            onChange={(e) => handlePortionChange(portion, "portion", e.target.value)}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment sx={{ "& p": { textTransform: "capitalize" } }} position="end">
                                  {meal?.portionUnit ? meal.portionUnit.toLowerCase() : ""}
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Calories
                          </Typography>
                          <TextField
                            className="form-field"
                            value={portion.calories?.value}
                            error={portionsError.show && portion.calories?.error}
                            helperText={
                              portionsError.show && portion.calories?.error ? portion.calories.message : undefined
                            }
                            onChange={(e) => handlePortionChange(portion, "calories", e.target.value)}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">Cal</InputAdornment>,
                            }}
                          />
                        </Box>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Protein
                          </Typography>
                          <TextField
                            className="form-field"
                            value={portion.protein?.value}
                            error={portionsError.show && portion.protein?.error}
                            helperText={
                              portionsError.show && portion.protein?.error ? portion.protein.message : undefined
                            }
                            onChange={(e) => handlePortionChange(portion, "protein", e.target.value)}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">Gr</InputAdornment>,
                            }}
                            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                          />
                        </Box>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Carbs
                          </Typography>
                          <TextField
                            className="form-field"
                            value={portion.carbs?.value}
                            error={portionsError.show && portion.carbs?.error}
                            helperText={portionsError.show && portion.carbs?.error ? portion.carbs.message : undefined}
                            onChange={(e) => handlePortionChange(portion, "carbs", e.target.value)}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">Gr</InputAdornment>,
                            }}
                            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                          />
                        </Box>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Fat
                          </Typography>
                          <TextField
                            className="form-field"
                            value={portion.fat?.value}
                            error={portionsError.show && portion.fat?.error}
                            helperText={portionsError.show && portion.fat?.error ? portion.fat.message : undefined}
                            onChange={(e) => handlePortionChange(portion, "fat", e.target.value)}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">Gr</InputAdornment>,
                            }}
                            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                          />
                        </Box>
                        {portions.length > 1 && (
                          <Box className="form-section" sx={{ flex: 0 }}>
                            <IconButton
                              sx={{
                                backgroundColor: "text.gray",
                                color: "white",
                                padding: "12px",
                                width: "50px",
                                height: "50px",
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "text.gray2" },
                              }}
                              onClick={() => deletePortion(portion)}
                            >
                              <Delete2Icon width={24} height={24} />
                            </IconButton>
                          </Box>
                        )}
                        {key === 0 && (
                          <Box className="form-section" sx={{ flex: 0 }}>
                            <IconButton
                              sx={{
                                backgroundColor: "secondary.main",
                                color: "white",
                                padding: "12px",
                                width: "50px",
                                height: "50px",
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "secondary.dark" },
                              }}
                              onClick={addPortion}
                            >
                              <PlusSignIcon />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>

              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Ingredients
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    border: "1px solid",
                    borderColor: "text.light3",
                    borderRadius: "8px",
                    padding: "0px 12px 0px 0",
                  }}
                >
                  <Ingredients options={ingredientList} onChange={(...args) => handleIngredientChange(...args)} />
                  <TextField
                    className="form-field"
                    sx={{
                      "& .MuiInputBase-root": { height: "32px" },
                      width: "110px",
                      "& .Mui-error fieldset": { borderColor: "#ff4040 !important" },
                      "& .MuiFormHelperText-root": { position: "absolute", top: "25px", fontSize: "10px" },
                    }}
                    value={portionOnIngredient}
                    error={portionOnIngredientError.show && portionOnIngredientError.error}
                    helperText={
                      portionOnIngredientError.show && portionOnIngredientError.error
                        ? portionOnIngredientError.message
                        : undefined
                    }
                    onChange={(e) => handlePortionOnIngredient(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment sx={{ "& p": { textTransform: "capitalize" } }} position="end">
                          Gr
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      padding: "0 6px",
                      borderRadius: "6px",
                      backgroundColor: "text.tableColor",
                      height: "32px",
                    }}
                  >
                    <FormControlLabel
                      sx={{
                        margin: 0,
                        "& .MuiFormControlLabel-label": { fontSize: "10px", fontWeight: 500 },
                        gap: "4px",
                        "& svg": { width: "16px", height: "16px" },
                        "& span:last-child": { lineHeight: "16px", fontSize: "10px" },
                      }}
                      control={
                        <Checkbox
                          sx={{ padding: 0 }}
                          checked={portionRemovableValue}
                          onChange={(e) => setPortionRemovableValue((state) => !state)}
                        />
                      }
                      label="Removable"
                    />
                  </Box>
                  <Button
                    sx={{ width: "auto", height: "32px", fontSize: "12px", fontWeight: 500 }}
                    color="primary"
                    variant="contained"
                    disableElevation
                    onClick={addIngredient}
                  >
                    Add
                  </Button>
                </Box>

                <Box
                  className="tagging-container"
                  sx={{
                    width: "100%",
                    padding: 0,
                    minHeight: "auto !important",
                  }}
                >
                  {ingredients.map((ingredient) => (
                    <Box
                      key={ingredient.id}
                      className="tagging"
                      sx={{
                        display: "flex !important",
                        borderRadius: "8px !important",
                        padding: "6px 8px !important",
                        alignItems: "center",
                        columnGap: "8px !important",
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Typography sx={{ fontSize: "10px !important", fontWeight: 600 }}>
                            {checkIf(ingredient.amount)
                              ? `${ingredient.name} - ${ingredient.amount}gr`
                              : `${ingredient.name}`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex" }}>
                          <FormControlLabel
                            sx={{
                              margin: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: "10px",
                                fontWeight: 600,
                                color: "primary.main",
                              },
                              gap: "4px",
                              "& svg": { width: "14px", height: "14px" },
                              "& span:last-child": { lineHeight: "16px", fontSize: "10px" },
                            }}
                            control={
                              <Checkbox
                                sx={{ padding: 0, color: "primary.main" }}
                                checked={ingredient.removable}
                                onChange={(e) => handleIngredientRemovable(e, ingredient)}
                                name={ingredient.id}
                              />
                            }
                            label="Removable"
                          />
                        </Box>
                      </Box>

                      <CloseIconCircle onClick={() => deleteIngredient(ingredient)} />
                    </Box>
                  ))}
                </Box>
                {/* <Box width={200} sx={{ marginTop: "4px" }}>
                  <Tagging
                    input={ingredientList}
                    placeholder="Add Ingredient"
                    onChange={(...args) => updateIngredients(...args)}
                  />
                </Box> */}
              </Box>

              <Box sx={{ display: "flex", gap: "12px" }}>
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Tags
                  </Typography>
                  <TextField
                    className="form-field"
                    value={meal?.tags?.join(",")}
                    error={validation.show && validation.tags?.error}
                    helperText={
                      validation.show && validation.tags?.error
                        ? validation.tags.helperText
                        : "Separate the entries with comma"
                    }
                    onChange={(e) => handleFieldChange("tags", e.target.value)}
                  />
                </Box>

                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    &nbsp;
                  </Typography>
                  <FormControlLabel
                    sx={{
                      margin: 0,
                      columnGap: "8px",
                      "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                    }}
                    control={
                      <Toggle
                        checked={Boolean(meal?.portionCustomizable)}
                        onChange={(e) => handleFieldChange("portionCustomizable", Boolean(e.target.checked))}
                      />
                    }
                    label="Set the portion as customizable"
                  />
                </Box>
              </Box>

              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Allergens
                </Typography>
                <Box
                  className="tagging-container"
                  sx={{
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #e5e7ea",
                    padding: "8px",
                  }}
                >
                  {allergens.map((allergen) => (
                    <Box key={allergen} className="tagging">
                      <Typography>{allergen}</Typography>
                      <CloseIconCircle onClick={() => deleteAllergen(allergen)} />
                    </Box>
                  ))}
                </Box>
                <Box width={200} sx={{ marginTop: "4px" }}>
                  <Tagging
                    input={allergenList}
                    placeholder="Add Allergen"
                    onChange={(...args) => updateAllergens(...args)}
                  />
                </Box>
              </Box>
            </Box>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <LoadingButton
            fullWidth
            loading={loading}
            color="secondary"
            variant="contained"
            sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
            onClick={saveChanges}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
